import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash.debounce";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import { connect, useDispatch } from "react-redux";
import {
  d2cRegistrationAsync,
  fetchUserAsync,
  setToken,
} from "../../features/auth/authSlice";
import { toggleOverlay } from "../../store/slices/headerSlice";
import { trackEvent } from "../../analytics/gtag";
import { EVENTS } from "../../analytics/events";

const SignupPage = ({ setTab, toggleOverlay }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [enteredOTP, setEnteredOTP] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpLoading, setOtpLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const whatsappRef = useRef(null);
  const submitButtonRef = useRef(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    whatsappUpdates: false,
  });

  const handlePhoneChange = (e) => {
    if (!otpSent && phoneNumber.length === 0) {
      // Analytics
      trackEvent({
        action: EVENTS.ONBOARDING_STEP_1_STARTED,
        category: "Onboarding",
        label: "User started entering phone number",
      });
    }
    setOtpSent(false);
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneNumber(digitsOnly);
  };

  useEffect(() => {
    const checkPhone = async () => {
      if (phoneNumber.length !== 10) {
        setErrors((prev) => ({ ...prev, Phone: "Invalid phone number" }));
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}singup_phone_verify/${phoneNumber}`
        );
        const data = await res.json();

        if (data.status === "error") {
          setErrors((prev) => ({
            ...prev,
            Phone: "Ph No. already registered with Cyclecircle",
          }));
        } else {
          setErrors((prev) => ({ ...prev, Phone: "" }));
        }
      } catch (err) {
        // console.error("Phone check failed:", err);
      }
    };

    const debouncedCheck = debounce(checkPhone, 500);
    if (phoneNumber?.length === 10) debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [phoneNumber]);

  useEffect(() => {
    if (phoneNumber?.length < 10) {
      setErrors({});
    }
  }, [phoneNumber]);

  useEffect(() => {
    const email = form.email;

    const checkEmail = async () => {
      if (!isValidEmail(email)) {
        setErrors((prev) => ({
          ...prev,
          Email: "Please enter a valid email address",
        }));
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}singup_email_verify/${email}`
        );
        const data = await response.json();

        if (data.status === "error") {
          setEmailExists(true);
          setErrors((prev) => ({
            ...prev,
            Email: "Email already registered with Cyclecircle",
          }));
        } else {
          setEmailExists(false);
          setErrors((prev) => ({ ...prev, Email: "" }));
        }
      } catch (error) {
        // console.error("Error checking email existence:", error);
      }
    };

    const debouncedCheck = debounce(checkEmail, 500);
    if (email) debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [form.email]);

  const handlePhoneCheckAndSendOTP = async () => {
    if (phoneNumber?.length !== 10) {
      setErrors({ Phone: "Please enter a valid 10-digit phone number" });
      return;
    }

    // Check if phone already exists
    try {
      // Send OTP
      setOtpLoading(true);
      const sendRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}phone_auth/${phoneNumber}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (sendRes.ok) {
        setOtpSent(true);
        toast.success("OTP sent successfully!");
      } else {
        toast.error("OTP not Sent");
      }
    } catch (error) {
      // console.error("OTP Send Error:", error.message);
      toast.error("Please try again later");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setOtpLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}phone_auth_otp/${phoneNumber}/${enteredOTP}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setStep(2);
        // Analytics
        trackEvent({
          action: EVENTS.ONBOARDING_STEP_1_COMPLETED,
          category: "Onboarding",
          label: "User verified phone number",
          value: phoneNumber
        });
        trackEvent({
          action: EVENTS.ONBOARDING_STEP_2_STARTED,
          category: "Onboarding",
          label: "User Entering First Name",
          value: phoneNumber
        });

        toast.success("Phone number verified!");
      } else {
        setErrors({ OTP: "Please enter a valid 6-digit OTP." });
        toast.error("Invalid OTP");
      }
    } catch (error) {
      // console.error("OTP Verify Error:", error.message);
      toast.error("Something went wrong");
    } finally {
      setOtpLoading(false);
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailBlur = async (e) => {
    const email = e.target.value;
    if (!isValidEmail(email)) {
      setErrors((prev) => ({
        ...prev,
        Email: "Please enter a valid email address",
      }));
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}singup_email_verify/${email}`
      );
      const data = await response.json();

      if (data.status === "error") {
        setEmailExists(true);
        setErrors((prev) => ({
          ...prev,
          Email: "Email already registered with Cyclecircle",
        }));
      } else {
        setEmailExists(false);
        setErrors((prev) => ({ ...prev, Email: "" }));
      }
    } catch (error) {
      // console.error("Error checking email existence:", error);
    }
  };

  const handleRegister = async () => {
    const { firstName, lastName, email, whatsappUpdates } = form;

    if (!firstName || !lastName || !email || !phoneNumber) {
      setErrors({ General: "Please fill in all fields." });
      return;
    }

    if (!isValidEmail(email)) {
      setErrors({ Email: "Please enter a valid email address" });
      return;
    }

    if (emailExists) return;

    try {
      setRegisterLoading(true);

      const registerData = {
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        PhoneNumber: phoneNumber,
        ReceiveWhatsApp: whatsappUpdates,
      };

      // console.log(registerData);

      // Replace with actual dispatch and await result if async thunk returns a promise
      await dispatch(d2cRegistrationAsync(registerData));

      // Analytics
      trackEvent({
        action: EVENTS.ONBOARDING_STEP_2_COMPLETED,
        category: "Onboarding",
        label: "User completed registration",
        value: phoneNumber
      });

      // Step 1: Send OTP (simulate what handlePhoneCheckAndSendOTP does)
      const sendOTPResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}login_number/${phoneNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!sendOTPResponse.ok) {
        toast.error("Failed to send OTP for login.");
        return;
      }

      // Step 2: Verify OTP using dummy code '282006'
      const dummyOTP = "282006";
      const verifyOTPResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}login_number_otp/${phoneNumber}/${dummyOTP}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await verifyOTPResponse.json();

      if (verifyOTPResponse.ok) {
        // toast.success("Logged in automatically after registration.");

        // Store token and user info
        document.cookie = `UserID=${data.UserID}`;
        document.cookie = `token=${data.token}`;
        await dispatch(setToken(data.token));
        dispatch(fetchUserAsync(data.UserID));

        // Reset state if needed
        // setPhoneNumber(null);
        toggleOverlay();
      } else {
        toast.error("Auto-login failed. Please login manually.");
        // console.error("OTP verification failed:", verifyOTPResponse.statusText);
      }
    } catch (error) {
      toast.error("Registration failed.");
    } finally {
      toast.success("Registered successfully!");
      setRegisterLoading(false);
    }
  };

  return (
    <div className="w-full flex justify-center items-center">
      <div className="text-white-1 rounded-2xl p-4 w-full max-w-md">
        <h2 className="text-2xl font-semibold mb-6 text-center">
          {step === 1 ? "Verify Phone Number" : "Create Account"}
        </h2>

        {step === 1 && (
          <>
            <input
              type="tel"
              placeholder="Phone Number"
              value={phoneNumber}
              onChange={handlePhoneChange}
              className={`w-full p-3 rounded bg-white-1 text-black-4 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                errors.Phone ? "mb-2" : "mb-4"
              }`}
            />
            {errors.Phone && (
              <p className="text-red-500 text-xs w-full text-left mb-2">
                {errors.Phone}
              </p>
            )}

            {otpSent && (
              <>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={enteredOTP}
                  onChange={(e) =>
                    setEnteredOTP(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  onKeyDown={(e) =>
                    (e.key === " " ||
                      (isNaN(Number(e.key)) && e.key.length === 1)) &&
                    e.preventDefault()
                  }
                  maxLength={6}
                  className={`w-full p-3 rounded bg-white-1 text-black-4 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                    errors.OTP ? "mb-2" : "mb-4"
                  }`}
                />
                {errors.OTP && (
                  <p className="text-red-500 text-xs w-full text-left mb-2">
                    {errors.OTP}
                  </p>
                )}
              </>
            )}

            <button
              onClick={otpSent ? handleVerifyOTP : handlePhoneCheckAndSendOTP}
              className={`w-full py-3 rounded font-semibold flex justify-center items-center ${
                enteredOTP?.length !== 6 && otpSent
                  ? "cursor-not-allowed opacity-50 bg-yellow-400"
                  : "hover:bg-yellow-500 bg-yellow-400"
              }`}
              disabled={otpLoading || (otpSent && enteredOTP?.length !== 6)}
            >
              {otpLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : otpSent ? (
                "Verify OTP"
              ) : (
                "Send OTP"
              )}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                placeholder="First Name"
                ref={firstNameRef}
                onKeyDown={(e) =>
                  e.key === "Enter" && lastNameRef.current.focus()
                }
                value={form.firstName}
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
                className="p-3 rounded bg-white-1 text-black-4 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />

              <input
                type="text"
                placeholder="Last Name"
                ref={lastNameRef}
                onKeyDown={(e) => e.key === "Enter" && emailRef.current.focus()}
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="p-3 rounded bg-white-1 text-black-4 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>

            <input
              type="tel"
              value={phoneNumber}
              disabled
              className="w-full mb-4 p-3 rounded bg-white-1 text-black-4 border border-gray-600 cursor-not-allowed"
            />

            <input
              type="email"
              placeholder="example@gmail.com"
              ref={emailRef}
              onKeyDown={(e) =>
                e.key === "Enter" && whatsappRef.current.click()
              }
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value.replace(/\s/g, "") })
              }
              onBlur={handleEmailBlur}
              className={`w-full p-3 rounded bg-white-1 text-black-4 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                errors.Email ? "mb-2" : "mb-4"
              }`}
            />
            {errors.Email && (
              <p className="text-red-500 text-xs w-full text-left mb-2">
                {errors.Email}
              </p>
            )}

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="whatsapp"
                ref={whatsappRef}
                onKeyDown={(e) =>
                  e.key === "Enter" && submitButtonRef.current.click()
                }
                checked={form.whatsappUpdates}
                onChange={(e) =>
                  setForm({ ...form, whatsappUpdates: e.target.checked })
                }
                className="w-4 h-4 mr-2 accent-yellow-400"
              />
              <label htmlFor="whatsapp" className="text-sm">
                Receive updates via WhatsApp
              </label>
            </div>

            <button
              ref={submitButtonRef}
              onClick={handleRegister}
              className="w-full bg-yellow-400 hover:bg-yellow-500 font-semibold py-3 rounded mb-4 flex justify-center items-center"
              disabled={registerLoading}
            >
              {registerLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Create Account"
              )}
            </button>
          </>
        )}

        {errors.General && (
          <p className="text-red-500 text-xs w-full text-left mb-2">
            {errors.General}
          </p>
        )}

        <p className="text-center text-sm my-2">
          Already a customer?{" "}
          <span
            className="text-yellow-400 cursor-pointer hover:underline"
            onClick={() => setTab("login")}
          >
            Sign in
          </span>
        </p>

        <div className="text-xs text-gray-400 text-center space-y-1">
          <p>Having trouble? Feel free to reach us</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <span>
              <LocalPhoneIcon style={{ fontSize: "18px" }} /> +91-8839478882
            </span>
            <span>
              <EmailIcon style={{ fontSize: "18px" }} /> support@cyclecircle.one
            </span>
          </div>
          <p>
            <Link
              to="/support"
              className="underline [text-underline-offset:3px]"
              onClick={toggleOverlay}
            >
              Privacy Policy
            </Link>{" "}
            &{" "}
            <Link
              to="/support"
              className="underline [text-underline-offset:3px]"
              onClick={toggleOverlay}
            >
              Terms of Service
            </Link>{" "}
            apply.
          </p>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
});

const mapDispatchToProps = {
  toggleOverlay,
};

export default connect(mapStateToProps, mapDispatchToProps)(SignupPage);
